import {
  Building2,
  CalendarClock,
  FileAudio,
  History,
  Layers3,
  MapPinned,
  MonitorSmartphone,
  RadioTower,
  Megaphone,
  ShieldCheck,
  UserCog,
  Users,
  LucideIcon,
} from 'lucide-react';
import { ComponentType, lazy } from 'react';
import { PERMISSIONS } from '@/modules/authentication/permissions';

const Monitoring = lazy(() => import('@/modules/iot/monitoring'));
const Groups = lazy(() => import('@/modules/iot/groups'));
const Devices = lazy(() => import('@/modules/iot/devices'));
const Peripherals = lazy(() => import('@/modules/iot/peripherals'));
const UsersView = lazy(() => import('@/modules/iot/users'));
const Roles = lazy(() => import('@/modules/iot/roles'));
const Organizations = lazy(() => import('@/modules/iot/organizations'));
const PowerStatistics = lazy(() => import('@/modules/iot/power-statistics'));
const PowerSettings = lazy(() => import('@/modules/iot/power-settings'));
const SipCall = lazy(() => import('@/modules/iot/sip-call'));
const SipCalls = lazy(() => import('@/modules/iot/sip-calls'));
const LiveAnnouncement = lazy(() => import('@/modules/iot/live-announcement'));
const BroadcastSchedules = lazy(() => import('@/modules/iot/broadcast-schedules'));
const RecordingSchedules = lazy(() => import('@/modules/iot/recording-schedules'));
const BroadcastContents = lazy(() => import('@/modules/iot/broadcast-contents'));

export type MenuItemType = {
  title: string;
  url: string;
  external?: string;
  icon?: LucideIcon;
  isActive?: boolean;
  items?: MenuItemType[];
  permission?: string | string[];
};

export type MenuType = MenuItemType[];
export type RouteConfigType = {
  path: string;
  component: ComponentType<Record<string, unknown>>;
  title: string;
  requiresAuth?: boolean;
  permission?: string | string[];
};

export const routeConfig: RouteConfigType[] = [
  { path: '/monitoring', component: Monitoring, title: 'Giám sát', requiresAuth: true, permission: PERMISSIONS.VIEW_MONITORING },
  { path: '/device-groups', component: Groups, title: 'Quản lý nhóm', requiresAuth: true, permission: PERMISSIONS.VIEW_DEVICE_GROUPS },
  { path: '/devices', component: Devices, title: 'Quản lý thiết bị', requiresAuth: true, permission: PERMISSIONS.VIEW_DEVICES },
  { path: '/sip-call', component: SipCall, title: 'Gọi SIP', requiresAuth: true, permission: PERMISSIONS.CREATE_SIP_CALLS },
  { path: '/sip-calls', component: SipCalls, title: 'Quản lý cuộc gọi', requiresAuth: true, permission: PERMISSIONS.VIEW_SIP_CALLS },
  { path: '/live-announcement', component: LiveAnnouncement, title: 'Phát thông báo', requiresAuth: true, permission: PERMISSIONS.VIEW_ANNOUNCEMENTS },
  { path: '/broadcast-schedules', component: BroadcastSchedules, title: 'Đặt lịch phát thanh', requiresAuth: true, permission: [PERMISSIONS.VIEW_BROADCAST_SCHEDULES, PERMISSIONS.MANAGE_BROADCAST_SCHEDULES] },
  { path: '/recording-schedules', component: RecordingSchedules, title: 'Đặt lịch ghi âm', requiresAuth: true, permission: [PERMISSIONS.VIEW_BROADCAST_SCHEDULES, PERMISSIONS.MANAGE_BROADCAST_SCHEDULES] },
  { path: '/broadcast-contents', component: BroadcastContents, title: 'Biên tập chương trình', requiresAuth: true, permission: [PERMISSIONS.VIEW_BROADCAST_CONTENTS, PERMISSIONS.MANAGE_BROADCAST_CONTENTS] },
  { path: '/devices/:deviceId/power/:peripheralId', component: PowerStatistics, title: 'Thống kê nguồn điện', requiresAuth: true, permission: PERMISSIONS.VIEW_PERIPHERALS },
  { path: '/devices/:deviceId/power/:peripheralId/settings', component: PowerSettings, title: 'Cài đặt thông số nguồn', requiresAuth: true, permission: PERMISSIONS.CONTROL_DEVICES },
  { path: '/peripherals', component: Peripherals, title: 'Thiết bị ngoại vi', requiresAuth: true, permission: PERMISSIONS.VIEW_PERIPHERALS },
  { path: '/users', component: UsersView, title: 'Người dùng', requiresAuth: true, permission: PERMISSIONS.VIEW_USERS },
  { path: '/roles', component: Roles, title: 'Vai trò', requiresAuth: true, permission: PERMISSIONS.VIEW_ROLES },
  { path: '/organizations', component: Organizations, title: 'Tổ chức', requiresAuth: true, permission: PERMISSIONS.VIEW_ORGANIZATIONS },
];

export const navMain: MenuType = [
  {
    title: 'Giám sát',
    url: '/monitoring',
    icon: MapPinned,
    permission: PERMISSIONS.VIEW_MONITORING,
  },
  {
    title: 'Phát thanh',
    url: '',
    icon: RadioTower,
    items: [
      { title: 'Quản lý cuộc gọi', url: '/sip-calls', icon: History, permission: PERMISSIONS.VIEW_SIP_CALLS },
      { title: 'Phát thông báo', url: '/live-announcement', icon: Megaphone, permission: PERMISSIONS.VIEW_ANNOUNCEMENTS },
      { title: 'Đặt lịch phát thanh', url: '/broadcast-schedules', icon: CalendarClock, permission: [PERMISSIONS.VIEW_BROADCAST_SCHEDULES, PERMISSIONS.MANAGE_BROADCAST_SCHEDULES] },
      { title: 'Đặt lịch ghi âm', url: '/recording-schedules', icon: CalendarClock, permission: [PERMISSIONS.VIEW_BROADCAST_SCHEDULES, PERMISSIONS.MANAGE_BROADCAST_SCHEDULES] },
      { title: 'Biên tập chương trình', url: '/broadcast-contents', icon: FileAudio, permission: [PERMISSIONS.VIEW_BROADCAST_CONTENTS, PERMISSIONS.MANAGE_BROADCAST_CONTENTS] },
    ],
  },
  {
    title: 'Quản lý',
    url: '',
    icon: UserCog,
    items: [
      { title: 'Quản lý nhóm', url: '/device-groups', icon: Layers3, permission: PERMISSIONS.VIEW_DEVICE_GROUPS },
      { title: 'Quản lý thiết bị', url: '/devices', icon: MonitorSmartphone, permission: PERMISSIONS.VIEW_DEVICES },
      { title: 'Tổ chức', url: '/organizations', icon: Building2, permission: PERMISSIONS.VIEW_ORGANIZATIONS },
      { title: 'Vai trò', url: '/roles', icon: ShieldCheck, permission: PERMISSIONS.VIEW_ROLES },
      { title: 'Người dùng', url: '/users', icon: Users, permission: PERMISSIONS.VIEW_USERS },
      { title: 'Thiết bị ngoại vi', url: '/peripherals', icon: RadioTower, permission: PERMISSIONS.VIEW_PERIPHERALS },
    ],
  },
];

export const routeTitles = routeConfig.reduce(
  (accumulator, route) => ({ ...accumulator, [route.path]: route.title }),
  {} as Record<string, string>,
);
